import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Star, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field } from "@/components/site/Common";
import { eur, fdate } from "@/lib/hz";
import { post, errMsg } from "@/lib/api";

export const useAction = () => {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(null);
  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      const r = await fn();
      qc.invalidateQueries({ queryKey: ["jobs"] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["earnings"] });
      if (okMsg) toast.success(okMsg);
      return r;
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(null); }
  };
  return { busy, run };
};

export const Box = ({ title, children, testid }) => (
  <div className="rounded-3xl border border-slate-200 bg-white p-6" data-testid={testid}>
    {title && <div className="font-heading text-lg font-bold">{title}</div>}
    <div className={title ? "mt-4" : ""}>{children}</div>
  </div>
);

export const QuotesList = ({ job, canAccept }) => {
  const { busy, run } = useAction();
  if (!job.quotes?.length) return <p className="text-sm text-slate-500">Todavía no hay presupuestos. Te avisaremos en cuanto llegue el primero.</p>;
  return (
    <div className="space-y-3" data-testid="quotes-list">
      {job.quotes.map((q) => (
        <div key={q.id} className="rounded-2xl border border-slate-200 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div><div className="font-semibold">{q.pro_name}</div><div className="flex items-center gap-1 text-xs text-slate-500"><Star className="size-3 fill-amber-400 text-amber-400" />{q.pro_rating?.toFixed(1)} · {q.days} {q.days === 1 ? "día" : "días"} · {fdate(q.created_at)}</div></div>
            <div className="font-heading text-xl font-extrabold">{eur(q.amount)}</div>
          </div>
          {q.message && <p className="mt-2 text-sm text-slate-600">{q.message}</p>}
          {canAccept && q.status === "pendiente" && (
            <Button size="sm" className="mt-3 rounded-lg" disabled={!!busy} data-testid={`accept-quote-${q.id}`}
              onClick={() => run(q.id, () => post(`/jobs/${job.id}/quotes/${q.id}/accept`), "¡Presupuesto aceptado! El profesional ya tiene tu trabajo.")}>
              {busy === q.id ? <Loader2 className="animate-spin" /> : <Check />}Aceptar presupuesto
            </Button>
          )}
          {q.status !== "pendiente" && <div className="mt-2 text-xs font-semibold capitalize text-slate-500">{q.status}</div>}
        </div>
      ))}
    </div>
  );
};

export const QuoteForm = ({ jobId }) => {
  const [f, setF] = useState({ amount: "", days: "1", message: "" });
  const { busy, run } = useAction();
  return (
    <form className="space-y-3" data-testid="quote-form" onSubmit={(e) => { e.preventDefault(); run("q", () => post(`/jobs/${jobId}/quotes`, { amount: Number(f.amount), days: Number(f.days), message: f.message }), "Presupuesto enviado al cliente"); }}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Importe (€)"><Input required type="number" min="1" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="h-11" data-testid="quote-amount-input" /></Field>
        <Field label="Plazo (días)"><Input required type="number" min="1" value={f.days} onChange={(e) => setF({ ...f, days: e.target.value })} className="h-11" data-testid="quote-days-input" /></Field>
      </div>
      <Field label="Mensaje"><Textarea rows={3} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="Qué incluye, disponibilidad…" data-testid="quote-message-input" /></Field>
      <Button type="submit" size="lg" className="w-full rounded-xl" disabled={!!busy} data-testid="quote-submit-btn">{busy && <Loader2 className="animate-spin" />}Enviar presupuesto</Button>
    </form>
  );
};

export const DirectRespond = ({ job }) => {
  const [amount, setAmount] = useState(job.budget ? String(job.budget) : "");
  const { busy, run } = useAction();
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">{job.client_name} te ha elegido directamente. Indica tu precio para aceptar.</p>
      <Field label="Precio (€)"><Input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-11" data-testid="direct-amount-input" /></Field>
      <div className="flex gap-2">
        <Button className="flex-1 rounded-xl" disabled={!!busy || !amount} data-testid="direct-accept-btn" onClick={() => run("a", () => post(`/jobs/${job.id}/respond`, { accept: true, amount: Number(amount) }), "Trabajo aceptado")}>Aceptar</Button>
        <Button variant="outline" className="flex-1 rounded-xl" disabled={!!busy} data-testid="direct-reject-btn" onClick={() => run("r", () => post(`/jobs/${job.id}/respond`, { accept: false }), "Solicitud rechazada")}>Rechazar</Button>
      </div>
    </div>
  );
};

export const InvoiceGenerator = ({ job }) => {
  const [lines, setLines] = useState([{ concept: `Mano de obra: ${job.title}`, quantity: 1, unit_price: job.price ?? 0 }]);
  const { busy, run } = useAction();
  const base = lines.reduce((a, l) => a + l.quantity * l.unit_price, 0);
  const iva = Math.round(base * 21) / 100;
  const upd = (i, patch) => setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  return (
    <div className="space-y-3" data-testid="invoice-generator">
      {lines.map((l, i) => (
        <div key={i} className="grid grid-cols-[1fr_70px_100px] gap-2">
          <Input value={l.concept} onChange={(e) => upd(i, { concept: e.target.value })} className="h-10" data-testid={`invoice-line-concept-${i}`} />
          <Input type="number" min="1" value={l.quantity} onChange={(e) => upd(i, { quantity: Number(e.target.value) })} className="h-10" />
          <Input type="number" min="0" step="0.01" value={l.unit_price} onChange={(e) => upd(i, { unit_price: Number(e.target.value) })} className="h-10" data-testid={`invoice-line-price-${i}`} />
        </div>
      ))}
      <button type="button" className="text-sm font-semibold text-orange-600" onClick={() => setLines([...lines, { concept: "Material", quantity: 1, unit_price: 0 }])} data-testid="invoice-add-line">+ Añadir línea</button>
      <div className="space-y-1 rounded-xl bg-slate-50 p-3 text-sm">
        <div className="flex justify-between"><span>Base imponible</span><span>{eur(base)}</span></div>
        <div className="flex justify-between"><span>IVA 21%</span><span>{eur(iva)}</span></div>
        <div className="flex justify-between font-bold"><span>Total</span><span>{eur(base + iva)}</span></div>
      </div>
      <Button className="w-full rounded-xl" disabled={!!busy} data-testid="invoice-submit-btn" onClick={() => run("i", () => post(`/jobs/${job.id}/invoice`, { lines }), "Factura emitida y enviada al cliente")}>{busy && <Loader2 className="animate-spin" />}Emitir factura</Button>
    </div>
  );
};
